import React, { useState, useMemo } from 'react';
import useGameStore from '../../store/gameStore';
import { BANKS } from '../../data/bankData';
import { Truck, Clock, CheckCircle2, XCircle } from 'lucide-react';

const DURATIONS = [24, 36, 48];

/**
 * 13/07/2026 — Arc « crédit véhicule » (événement confrère retraité).
 * Dossier spécial affiché en haut de la page Banque tant que
 * `pendingVehicleFinancing` est actif : l'apprenant compare les
 * propositions des 3 banques (taux, mensualité, coût total), choisit une
 * durée et accepte — l'achat du véhicule réservé se conclut alors
 * automatiquement (crédit affecté, gagé sur le véhicule).
 */
export function VehicleFinancingCard() {
  const pendingVehicleFinancing = useGameStore(s => s.pendingVehicleFinancing);
  const totalDays = useGameStore(s => s.totalDays);
  const requestVehicleFinancingProposals = useGameStore(s => s.requestVehicleFinancingProposals);
  const acceptVehicleFinancing = useGameStore(s => s.acceptVehicleFinancing);
  const declineVehicleFinancing = useGameStore(s => s.declineVehicleFinancing);
  const [duration, setDuration] = useState(36);

  const proposals = useMemo(
    () => (pendingVehicleFinancing && typeof requestVehicleFinancingProposals === 'function'
      ? requestVehicleFinancingProposals(duration)
      : []),
    [pendingVehicleFinancing, duration, requestVehicleFinancingProposals],
  );

  if (!pendingVehicleFinancing) return null;
  const pvf = pendingVehicleFinancing;
  const daysLeft = Math.max(0, (pvf.expiresDay || 0) - (totalDays || 0));
  const bestMonthly = proposals.length ? Math.min(...proposals.map(p => p.monthly)) : null;

  return (
    <div
      className="rounded-xl border-2 border-amber-500/50 bg-amber-500/5 p-4 space-y-3"
      data-testid="vehicle-financing-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-500/15 flex items-center justify-center">
            <Truck className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-amber-400">
              Dossier spécial — Crédit véhicule (gagé)
            </p>
            <h3 className="text-sm font-bold text-foreground">
              {pvf.brand} {pvf.model} du confrère retraité — {(pvf.price || 0).toLocaleString('fr-FR')} €
            </h3>
          </div>
        </div>
        <div
          className="flex items-center gap-1.5 text-[11px] font-mono text-amber-300 bg-amber-500/10 px-2.5 py-1 rounded-full"
          data-testid="vehicle-financing-days-left"
        >
          <Clock className="w-3 h-3" />
          Réservé encore {daysLeft} j
        </div>
      </div>

      <p className="text-[11px] text-slate-400 leading-relaxed">
        Le prêt est <strong className="text-slate-200">gagé sur le véhicule</strong> : les banques
        acceptent malgré votre jeune historique (exception à la règle des 3 mois). Comparez le
        <strong className="text-slate-200"> taux</strong>, la <strong className="text-slate-200">mensualité</strong> et
        le <strong className="text-slate-200">coût total</strong>, puis acceptez la meilleure offre —
        l'achat se conclut automatiquement.
      </p>

      {/* Choix de durée */}
      <div className="flex items-center gap-2">
        <span className="text-[11px] text-slate-400">Durée :</span>
        {DURATIONS.map(m => (
          <button
            key={m}
            onClick={() => setDuration(m)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
              duration === m ? 'bg-amber-500 text-black' : 'bg-ink-800 text-slate-400 border border-ink-600'
            }`}
            data-testid={`vehicle-financing-duration-${m}`}
          >
            {m} mois
          </button>
        ))}
      </div>

      {/* Propositions des 3 banques */}
      <div className="grid sm:grid-cols-3 gap-2">
        {proposals.map(p => (
          <div
            key={p.bankId}
            className={`rounded-lg border p-3 space-y-1.5 ${
              p.monthly === bestMonthly ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-ink-600 bg-ink-800/60'
            }`}
            data-testid={`vehicle-financing-proposal-${p.bankId}`}
          >
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold text-foreground">{BANKS[p.bankId]?.name}</p>
              {p.monthly === bestMonthly && (
                <span className="text-[10px] font-bold text-emerald-400 uppercase">Meilleure offre</span>
              )}
            </div>
            <p className="text-lg font-mono font-bold text-amber-300">{p.ratePercent} %</p>
            <p className="text-[11px] text-slate-400">
              {p.monthly.toLocaleString('fr-FR')} €/mois · coût total {p.totalCost.toLocaleString('fr-FR')} €
            </p>
            <p className="text-[10px] text-slate-500">
              dont {p.totalInterest.toLocaleString('fr-FR')} € d'intérêts
            </p>
            <button
              onClick={() => acceptVehicleFinancing(p.bankId, duration)}
              className="w-full mt-1 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold flex items-center justify-center gap-1.5 transition-colors"
              data-testid={`vehicle-financing-accept-${p.bankId}`}
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Accepter et acheter
            </button>
          </div>
        ))}
      </div>

      <button
        onClick={() => declineVehicleFinancing()}
        className="text-[11px] text-slate-500 hover:text-slate-300 flex items-center gap-1 transition-colors"
        data-testid="vehicle-financing-decline"
      >
        <XCircle className="w-3 h-3" /> Renoncer au véhicule
      </button>
    </div>
  );
}
